import axios from "axios";
import reduxStore from "../../utils/redux/store";
import authSlice from "../../utils/redux/slice/authSlice";
import constants from "../../configs/constants";
import { message } from "antd";

const secureAxios = axios.create({
  baseURL: constants.API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

secureAxios.interceptors.request.use((config) => {
  const token = reduxStore.store.getState().auth.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let refreshQueue = [];

// redux-persist double-serializes: persist:root is JSON whose slice values are
// themselves JSON strings. Long-lived tabs never rehydrate, so this is the only
// way a tab can see tokens another tab rotated.
function readPersistedAuth() {
  try {
    const root = JSON.parse(localStorage.getItem("persist:root") || "{}");
    return root.auth ? JSON.parse(root.auth) : null;
  } catch {
    return null;
  }
}

function adoptSession(persisted) {
  reduxStore.store.dispatch(authSlice.actions.setToken(persisted.token));
  reduxStore.store.dispatch(authSlice.actions.setRefreshToken(persisted.refreshToken));
  secureAxios.defaults.headers["Authorization"] = `Bearer ${persisted.token}`;
}

secureAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const original = error.config;

    // Handle 401 with token refresh
    if (status === 401 && !original._retry) {
      const refreshToken = reduxStore.store.getState().auth.refreshToken;

      // No refresh token available — logout immediately
      if (!refreshToken) {
        // Inline (not clearProfileCache import — that would cycle back into this module)
        try { localStorage.removeItem("uro_cached_balance"); } catch {}
        reduxStore.store.dispatch(authSlice.actions.logout());
        message.error("Session expired. Please login again.");
        window.location.href = "/login";
        return Promise.reject(error.response?.data || error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((newToken) => {
          original.headers["Authorization"] = `Bearer ${newToken}`;
          return secureAxios(original);
        }).catch((err) => Promise.reject(err));
      }

      original._retry = true;
      isRefreshing = true;

      try {
        // Multi-tab: another tab may have already rotated this refresh token.
        // Presenting the stale one would 401 — and outside the server's grace
        // window it trips reuse detection and revokes EVERY session. Adopt the
        // newer persisted session instead of refreshing at all.
        const persistedPre = readPersistedAuth();
        if (persistedPre?.refreshToken && persistedPre.refreshToken !== refreshToken && persistedPre.token) {
          adoptSession(persistedPre);
          refreshQueue.forEach(({ resolve }) => resolve(persistedPre.token));
          refreshQueue = [];
          original.headers["Authorization"] = `Bearer ${persistedPre.token}`;
          return secureAxios(original);
        }

        const { data } = await axios.post(
          `${constants.API_URL}/auth/refresh`,
          { refreshToken },
          { headers: { "Content-Type": "application/json" } }
        );

        const newToken = data.data?.token || data.token;
        const newRefreshToken = data.data?.refreshToken || data.refreshToken;

        reduxStore.store.dispatch(authSlice.actions.setToken(newToken));
        if (newRefreshToken) {
          reduxStore.store.dispatch(authSlice.actions.setRefreshToken(newRefreshToken));
        }
        secureAxios.defaults.headers["Authorization"] = `Bearer ${newToken}`;

        refreshQueue.forEach(({ resolve }) => resolve(newToken));
        refreshQueue = [];

        original.headers["Authorization"] = `Bearer ${newToken}`;
        return secureAxios(original);
      } catch (refreshError) {
        // Only a REJECTED refresh (401) means the session is dead — a network
        // blip or 500 must not purge the session and force a re-login
        if (refreshError?.response?.status === 401) {
          // Race fallback: rotation may have happened while our request was in
          // flight — adopt the other tab's session before purging
          const persisted = readPersistedAuth();
          if (persisted?.refreshToken && persisted.refreshToken !== refreshToken && persisted.token) {
            adoptSession(persisted);
            refreshQueue.forEach(({ resolve }) => resolve(persisted.token));
            refreshQueue = [];
            original.headers["Authorization"] = `Bearer ${persisted.token}`;
            return secureAxios(original);
          }
          refreshQueue.forEach(({ reject }) => reject(refreshError));
          refreshQueue = [];
          try { localStorage.removeItem("uro_cached_balance"); } catch {}
          reduxStore.store.dispatch(authSlice.actions.logout());
          message.error("Session expired. Please login again.");
          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
        refreshQueue.forEach(({ reject }) => reject(refreshError));
        refreshQueue = [];
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Only log unexpected errors (not 400/404 which are often expected during fallback logic)
    if (status && ![400, 404].includes(status)) {
      console.error("API Error:", error.response?.data || error.message);
    }

    return Promise.reject(error.response?.data || error);
  }
);

export default secureAxios;
